import type { ResourcesConfig } from 'aws-amplify';

/**
 * Amplify auth configuration for the Forge Cognito user pool.
 *
 * Values are read from Vite env vars so the same bundle works against dev,
 * staging, and prod pools. Missing values fall back to a placeholder that
 * keeps the build green — `signInWithRedirect()` will fail loudly at runtime
 * rather than ship a working flow against the wrong pool.
 *
 * Required env:
 *   VITE_COGNITO_USER_POOL_ID        e.g. eu-central-1_yHzoFRd6Y
 *   VITE_COGNITO_USER_POOL_CLIENT_ID e.g. 4vm99luo2k1s9qdvkq9ot4035k
 *   VITE_COGNITO_DOMAIN              e.g. forge-dev-331094056210.auth.eu-central-1.amazoncognito.com
 *   VITE_COGNITO_REGION              e.g. eu-central-1
 *   VITE_COGNITO_REDIRECT_SIGN_IN    e.g. http://localhost:5173/auth/callback
 *   VITE_COGNITO_REDIRECT_SIGN_OUT   e.g. http://localhost:5173/
 */

const env = import.meta.env;

const PLACEHOLDER = 'unconfigured.amazoncognito.com';

export const cognitoConfig = {
  userPoolId: (env.VITE_COGNITO_USER_POOL_ID as string | undefined) ?? 'eu-central-1_yHzoFRd6Y',
  userPoolClientId:
    (env.VITE_COGNITO_USER_POOL_CLIENT_ID as string | undefined) ?? '4vm99luo2k1s9qdvkq9ot4035k',
  region: (env.VITE_COGNITO_REGION as string | undefined) ?? 'eu-central-1',
  domain: (env.VITE_COGNITO_DOMAIN as string | undefined) ?? PLACEHOLDER,
  redirectSignIn:
    (env.VITE_COGNITO_REDIRECT_SIGN_IN as string | undefined) ??
    `${window.location.origin}/auth/callback`,
  redirectSignOut:
    (env.VITE_COGNITO_REDIRECT_SIGN_OUT as string | undefined) ?? `${window.location.origin}/`,
} as const;

export const amplifyConfig: ResourcesConfig = {
  Auth: {
    Cognito: {
      userPoolId: cognitoConfig.userPoolId,
      userPoolClientId: cognitoConfig.userPoolClientId,
      loginWith: {
        oauth: {
          domain: cognitoConfig.domain,
          scopes: ['openid', 'email', 'profile'],
          redirectSignIn: [cognitoConfig.redirectSignIn],
          redirectSignOut: [cognitoConfig.redirectSignOut],
          responseType: 'code',
        },
      },
    },
  },
};

export function isCognitoConfigured(): boolean {
  return cognitoConfig.domain !== PLACEHOLDER;
}
